import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import RecipeDetail from "../components/RecipeDetail";
import Button from "../components/Button";
import {
  deleteRecipe,
  getRecipeById,
  isRecipeSavedForUser,
  removeSavedRecipeForUser,
  saveRecipeForUser,
} from "../services/recipeServices";
import { getImage } from "../stores/imageStore";
import { useUser } from "../contexts/UserContext";
import TextArea from "../components/forms/TextArea";
import {
  getUserRecipeNote,
  saveUserRecipeNote,
} from "../services/notesService";

const ViewRecipe = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useUser();
  const [copyButtonText, setCopyButtonText] = useState("Copy JSON");
  const [isSaved, setIsSaved] = useState(false);
  const [isUpdatingSave, setIsUpdatingSave] = useState(false);
  const [isCookingModeEnabled, setIsCookingModeEnabled] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [initialNote, setInitialNote] = useState("");
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteLoadError, setNoteLoadError] = useState(null);
  const [noteSaveError, setNoteSaveError] = useState(null);
  const [noteSaveSuccess, setNoteSaveSuccess] = useState(false);
  const wakeLockRef = useRef(null);
  const isWakeLockSupported =
    typeof navigator !== "undefined" &&
    navigator.wakeLock &&
    typeof navigator.wakeLock.request === "function";

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
      } catch (error) {
        console.error("Error releasing wake lock:", error);
      } finally {
        wakeLockRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (!isCookingModeEnabled) {
      releaseWakeLock();
      return;
    }

    if (!isWakeLockSupported) {
      setIsCookingModeEnabled(false);
      return;
    }

    let isCancelled = false;
    let currentWakeLock = null;

    const handleRelease = () => {
      wakeLockRef.current = null;

      if (!isCancelled && document.visibilityState === "visible") {
        acquireWakeLock();
      }
    };

    async function acquireWakeLock() {
      try {
        const wakeLock = await navigator.wakeLock.request("screen");

        if (isCancelled) {
          try {
            await wakeLock.release();
          } catch (error) {
            console.error("Error releasing wake lock after cancellation:", error);
          }
          return;
        }

        wakeLockRef.current = wakeLock;
        currentWakeLock = wakeLock;
        wakeLock.addEventListener("release", handleRelease);
      } catch (error) {
        console.error("Error requesting wake lock:", error);
        if (!isCancelled) {
          setIsCookingModeEnabled(false);
        }
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };

    acquireWakeLock();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isCancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (currentWakeLock) {
        currentWakeLock.removeEventListener("release", handleRelease);
      }
      releaseWakeLock();
    };
  }, [isCookingModeEnabled, isWakeLockSupported, releaseWakeLock]);

  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  const handleToggleCookingMode = () => {
    if (!isWakeLockSupported) {
      return;
    }

    setIsCookingModeEnabled((prev) => !prev);
  };

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const fetchedRecipe = await getRecipeById(id);
        setRecipe(fetchedRecipe);

        if (fetchedRecipe) {
          if (fetchedRecipe.images?.length > 0) {
            const url = await getImage(fetchedRecipe.images[0]);
            setImageUrl(url);
          }
        }
      } catch (error) {
        console.error(`Error fetching recipe ${id}: ${error}`)
      }

      setLoading(false);
    }
    fetchRecipe();
  }, [id])

  useEffect(() => {
    let isMounted = true;

    const syncSavedState = async () => {
      if (!recipe || !user) {
        if (isMounted) {
          setIsSaved(false);
        }
        return;
      }

      try {
        const saved = await isRecipeSavedForUser(recipe.id, user.id);
        if (isMounted) {
          setIsSaved(saved);
        }
      } catch (error) {
        console.error("Error determining saved recipe state:", error);
        if (isMounted) {
          setIsSaved(false);
        }
      }
    };

    syncSavedState();

    return () => {
      isMounted = false;
    };
  }, [recipe, user]);

  useEffect(() => {
    let isMounted = true;

    const loadNote = async () => {
      if (!user || !recipe) {
        if (isMounted) {
          setNoteDraft("");
          setInitialNote("");
          setNoteLoadError(null);
          setNoteSaveError(null);
          setNoteSaveSuccess(false);
        }
        return;
      }

      if (isMounted) {
        setIsNotesLoading(true);
        setNoteLoadError(null);
        setNoteSaveError(null);
        setNoteSaveSuccess(false);
      }

      try {
        const noteData = await getUserRecipeNote(user.id, recipe.id);

        if (!isMounted) {
          return;
        }

        const noteText = noteData?.note ?? "";
        setNoteDraft(noteText);
        setInitialNote(noteText);
        setNoteSaveError(null);
        setNoteSaveSuccess(false);
      } catch (error) {
        console.error("Error loading recipe note:", error);

        if (isMounted) {
          setNoteLoadError("We couldn't load your note. You can still add a new one.");
        }
      } finally {
        if (isMounted) {
          setIsNotesLoading(false);
        }
      }
    };

    loadNote();

    return () => {
      isMounted = false;
    };
  }, [recipe, user]);

  useEffect(() => {
    if (!noteSaveSuccess) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setNoteSaveSuccess(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [noteSaveSuccess]);

  const handleCopyJson = () => {
    const jsonString = JSON.stringify(recipe, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopyButtonText("Copied!")
      setTimeout(() => setCopyButtonText("Copy JSON"), 2000);
    })
  }

  const handleNoteChange = (value) => {
    setNoteDraft(value);
    setNoteSaveError(null);
    setNoteSaveSuccess(false);
  };

  const handleDeleteRecipe = async () => {
    if (!window.confirm("Are you sure you want to delete this recipe?")) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteRecipe(recipe.id)
      navigate("/recipes");
    } catch (error) {
      console.error("Error deleting recipe:", error);
    } finally {
      setIsDeleting(false);
    }
  }

  const handleSaveNote = async () => {
    if (!user || !recipe) {
      return;
    }

    try {
      setIsSavingNote(true);
      setNoteSaveError(null);
      await saveUserRecipeNote(user.id, recipe.id, noteDraft);
      setInitialNote(noteDraft);
      setNoteSaveSuccess(true);
    } catch (error) {
      console.error("Error saving recipe note:", error);
      setNoteSaveError("We couldn't save your note. Please try again.");
    } finally {
      setIsSavingNote(false);
    }
  };

  const hasNoteChanges = noteDraft !== initialNote;

  const handleToggleSaveRecipe = async () => {
    if (!user || !recipe) {
      return;
    }

    try {
      setIsUpdatingSave(true);

      if (isSaved) {
        await removeSavedRecipeForUser(recipe.id, user.id);
        setIsSaved(false);
      } else {
        await saveRecipeForUser(recipe.id, user.id);
        setIsSaved(true);
      }
    } catch (error) {
      console.error("Error updating saved recipe state:", error);
    } finally {
      setIsUpdatingSave(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <p className="text-2xl text-greay-500">Loading recipe...</p>
      </div>
    )
  } else {
    if (!recipe) {
      return (
        <div className="flex flex-col gap-6 items-center">
          <h1 className="text-2xl text-red-500">Recipe not found :(</h1>
          <Button type="primary" onClick={() => navigate("/")}>
            Back to Recipes
          </Button>
        </div>
      )
    }

    const isJsonMode = searchParams.has("json");

    if (isJsonMode) {
      return (
        <div className="p-6">
          <h1 className="text-2xl text-primary font-bold mb-4">Recipe Json</h1>
          <pre className="bg-gray-100 p-4 rounded text-sm mb-4">
            {JSON.stringify(recipe, null, 2)}
          </pre>
          <div className="flex gap-4">
            <Button type="primary" onClick={handleCopyJson}>
              {copyButtonText}
            </Button>
            <Button type="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-12">
        <RecipeDetail
          recipe={recipe}
          imageUrl={imageUrl}
          mode="view"
          extraActions={
            <>
              <Button
                type={isCookingModeEnabled ? "accent" : "primary"}
                onClick={handleToggleCookingMode}
                disabled={!isWakeLockSupported}
                aria-pressed={isCookingModeEnabled}
                title={
                  !isWakeLockSupported
                    ? "Cooking mode isn't supported on this device."
                    : undefined
                }
              >
                {isCookingModeEnabled ? "Disable Cooking Mode" : "Enable Cooking Mode"}
              </Button>
              {user && (
                <Button
                  type={isSaved ? "secondary" : "primary"}
                  onClick={handleToggleSaveRecipe}
                  disabled={isUpdatingSave}
                  isLoading={isUpdatingSave}
                >
                  {isSaved ? "Unsave" : "Save"}
                </Button>
              )}
            </>
          }
        />
        {user && (
          <section className="print:hidden border border-gray-200 rounded-lg p-6 space-y-4 bg-white">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold">Personal Notes</h2>
              <p className="text-text-light mt-2">
                These notes are private to your account.
              </p>
            </div>
            {isNotesLoading ? (
              <p className="text-text-light">Loading your note...</p>
            ) : (
              <div className="space-y-4">
                <TextArea
                  value={noteDraft}
                  onChange={handleNoteChange}
                  placeholder="Add personal notes about this recipe..."
                  rows={6}
                  fullWidth
                  className="w-full min-h-[160px]"
                  disabled={isSavingNote}
                />
                <div className="flex flex-wrap items-center gap-4">
                  <Button
                    type="primary"
                    onClick={handleSaveNote}
                    disabled={!hasNoteChanges || isSavingNote}
                    isLoading={isSavingNote}
                  >
                    Save Notes
                  </Button>
                  {noteSaveSuccess && (
                    <span className="text-sm text-green-600">Saved!</span>
                  )}
                  {noteSaveError && (
                    <span className="text-sm text-red-500">{noteSaveError}</span>
                  )}
                </div>
                {noteLoadError && (
                  <p className="text-sm text-red-500">{noteLoadError}</p>
                )}
              </div>
            )}
          </section>
        )}
        {user && recipe.userId === user.id && (
          <div className="flex gap-4 print:hidden">
            <Button type="primary" onClick={() => navigate(`/recipe/${id}/edit`)} disabled={isDeleting}>
              Edit
            </Button>
            <Button type="secondary" onClick={handleDeleteRecipe} isLoading={isDeleting} disabled={isDeleting}>
              Delete
            </Button>
          </div>
        )}
      </div>
    )
  }
}

export default ViewRecipe;